import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Input, InputNumber, Select, Typography } from "antd";

const { Text } = Typography;
const { Option } = Select;

// 创建编辑上下文
const EditableContext = React.createContext(null);

// EditableRow 组件
export const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};

// EditableCell 组件
const EditableCell = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    inputType = "text",
    selectOptions = [],
    precision = 2,
    currency = "USD", // 接收币种属性，默认为USD
    ...restProps
}) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({ [dataIndex]: record[dataIndex] });
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({ ...record, ...values });
        } catch (errInfo) {
            console.log("Save failed:", errInfo);
        }
    };

    const renderInput = () => {
        const inputStyle = {
            width: "100%",
        };

        // 根据币种动态生成前缀和解析器
        const currencyPrefix = currency === "USD" ? "$" : currency;
        // 转义特殊字符以用于正则表达式
        const escapedPrefix = currencyPrefix.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
        const parserRegex = new RegExp(`${escapedPrefix}\\s?|(,*)`, "g");

        switch (inputType) {
            case "number":
                return (
                    <InputNumber
                        ref={inputRef}
                        style={{ ...inputStyle, textAlign: "right" }}
                        min={0}
                        precision={precision}
                        controls={false} // 隐藏增减按钮，避免遮挡数字
                        formatter={precision === 0 ? undefined : (value) => `${currencyPrefix} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        parser={precision === 0 ? undefined : (value) => value.replace(parserRegex, "")}
                        onPressEnter={save}
                        onBlur={save}
                    />
                );
            case "select":
                return (
                    <Select ref={inputRef} style={inputStyle} onBlur={save} allowClear showSearch filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                        {selectOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                );
            default:
                return <Input ref={inputRef} style={inputStyle} onPressEnter={save} onBlur={save} />;
        }
    };

    let childNode = children;

    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{
                    margin: 0,
                }}
                name={dataIndex}
                rules={[
                    {
                        required: false,
                        message: `${title} 是必填项！`,
                    },
                ]}
            >
                {renderInput()}
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap"
                style={{
                    padding: "4px 8px",
                    minHeight: "22px",
                    cursor: "pointer",
                }}
                onClick={toggleEdit}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                }}
            >
                <Text>{children}</Text>
            </div>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};

export default EditableCell;